import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import {activityStore} from "../../../../store/activity.store";
import style from "./style.module.css";
import {getCurrentActivities} from "./helper";
import CurrentActivity from "./components/CurrentActivity";
import {useIntl} from "react-intl";
import {UserSettings} from "../../../../model/auth.model";

dayjs.extend(duration);

const CurrentActivitiesList = ({ settings }: { settings?: UserSettings }) => {
  const activities = activityStore(state => state.activities);
  const activityTypes = activityStore(state => state.activityTypes);
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize current activities filtering
  const currentActivities = useMemo(() =>
    activities ? getCurrentActivities(activities) : [],
    [activities]
  );

  // O(1) lookup map for activity types
  const activityTypesMap = useMemo(() =>
    new Map(activityTypes?.map(t => [t.id, t]) || []),
    [activityTypes]
  );

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    };
    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  if (!activities || !activityTypes) {
    return null
  }

  const [firstActivity, ...restActivities] = currentActivities;

  return (
    <div className={style.activityList} ref={containerRef}>
      {firstActivity && (
        <div className={`${style.activityCard} ${expanded ? style.expanded : ''}`}>
          <CurrentActivity
            activityTypesMap={activityTypesMap}
            activityTypes={activityTypes}
            activity={firstActivity}
            settings={settings}
            hideComment
          />
          {(!!restActivities?.length || firstActivity.comment) && (
            <div className={style.moreRow}>
              {firstActivity.comment && (
                <span className={style.commentInline} title={firstActivity.comment}>{firstActivity.comment}</span>
              )}
              {!!restActivities?.length && (
                <span
                  className={style.more}
                  onClick={toggleExpanded}
                >
                  {expanded ? '−' : '+'}{restActivities.length}
                </span>
              )}
            </div>
          )}
          {expanded && restActivities.length > 0 && (
            <div className={style.expandedList}>
              {restActivities.map(activity => (
                <CurrentActivity
                  key={activity.id}
                  activityTypesMap={activityTypesMap}
                  activityTypes={activityTypes}
                  activity={activity}
                  settings={settings}
                  inPopover
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CurrentActivitiesList;
